import React, { useEffect, useState } from 'react';

interface VictoryEffectProps {
  winner: 'black' | 'white';
  isVisible: boolean;
  onHide?: () => void;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  velocity: { x: number; y: number };
  lifetime: number;
}

const VictoryEffect: React.FC<VictoryEffectProps> = ({ winner, isVisible, onHide }) => {
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (isVisible) {
      const colors = winner === 'black'
        ? ['#FFD700', '#FFA500', '#FF6347', '#FF1493', '#9370DB', '#4169E1']
        : ['#87CEEB', '#98FB98', '#DDA0DD', '#F0E68C', '#FFB6C1', '#E6E6FA'];

      const newParticles: Particle[] = [];

      // 创建礼花粒子
      for (let i = 0; i < 100; i++) {
        const angle = (Math.PI * 2 * i) / 100 + Math.random() * 0.2;
        const velocity = 5 + Math.random() * 10;

        newParticles.push({
          id: i,
          x: window.innerWidth / 2 + (Math.random() - 0.5) * 100,
          y: window.innerHeight / 2 + (Math.random() - 0.5) * 100,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3 + Math.random() * 5,
          velocity: {
            x: Math.cos(angle) * velocity,
            y: Math.sin(angle) * velocity - Math.random() * 5
          },
          lifetime: 60 + Math.random() * 40
        });
      }

      setParticles(newParticles);

      // 清理定时器
      const timer = setTimeout(() => {
        setParticles([]);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, winner]);

  useEffect(() => {
    if (particles.length === 0) return;

    const animationFrame = requestAnimationFrame(() => {
      setParticles(prevParticles =>
        prevParticles
          .map(particle => ({
            ...particle,
            x: particle.x + particle.velocity.x,
            y: particle.y + particle.velocity.y,
            velocity: {
              x: particle.velocity.x * 0.98,
              y: particle.velocity.y * 0.98 + 0.3 // 重力效果
            },
            lifetime: particle.lifetime - 1
          }))
          .filter(particle => particle.lifetime > 0)
      );
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [particles]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-50 cursor-pointer"
      onClick={() => {
        if (onHide) {
          onHide();
        }
      }}
    >
      {/* 关闭按钮 */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onHide) {
            onHide();
          }
        }}
        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm z-10"
        title="关闭"
      >
        ×
      </button>

      {/* 胜利文字动画 */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl md:text-8xl font-bold mb-4 victory-glow">
            {winner === 'black' ? '⚫' : '⚪'}
          </div>
          <div className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent victory-glow">
            🎉 胜利！🎉
          </div>
          <div className="text-xl md:text-2xl mt-2 text-gray-700 victory-text">
            {winner === 'black' ? '黑方' : '白方'}获胜！
          </div>
          <div className="text-sm md:text-base mt-4 text-gray-500 animate-pulse">
            点击任意位置继续游戏
          </div>
          <div className="mt-4 flex justify-center space-x-2">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-yellow-400 rounded-full sparkle"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 礼花粒子 */}
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}px`,
            top: `${particle.y}px`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            opacity: particle.lifetime / 100,
            transform: 'translate(-50%, -50%)',
            boxShadow: `0 0 ${particle.size}px ${particle.color}`,
          }}
        />
      ))}

      {/* 彩带飘落效果 */}
      {[...Array(20)].map((_, i) => (
        <div
          key={`ribbon-${i}`}
          className="absolute w-8 h-2 opacity-80 confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-20px',
            backgroundColor: ['#FFD700', '#FF69B4', '#87CEEB', '#98FB98', '#DDA0DD'][Math.floor(Math.random() * 5)],
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${3 + Math.random() * 2}s`
          }}
        />
      ))}

      {/* 闪光效果 */}
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-orange-500/20 animate-pulse pointer-events-none victory-background" />

      {/* 额外的星星效果 */}
      {[...Array(15)].map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute text-2xl animate-pulse"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 1}s`,
          }}
        >
          ✨
        </div>
      ))}
    </div>
  );
};

export default VictoryEffect;